import React, { useEffect, useState, useContext, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { format, differenceInCalendarDays, parseISO, startOfMonth, endOfMonth, isWithinInterval, subDays } from 'date-fns';
import { AuthContext } from '../AuthContext';
import StaffNav from './managernav';
import { ChevronDownIcon, DocumentArrowDownIcon } from '@heroicons/react/20/solid';

const SSP_DAILY = 23.75;
const UK_SSP_WEEKLY_CAP = 123.25;
const ZERO_HOURS_WEEKLY_THRESHOLD = 125;
const DEFAULT_QUALIFYING_DAYS = 5;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isZeroHoursContract = (contractType = '') => {
  const normalized = String(contractType || '').trim().toLowerCase();
  return normalized.includes('0 hour') || normalized.includes('zero hour') || normalized === '0';
};

function ClockInTable({ showNav = true }) {
  const [clockIns, setClockIns] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [endSourceMap, setEndSourceMap] = useState({});
  const [sick, setSick] = useState([]);
  const [holiday, setHoliday] = useState([]);
  const [payPolicies, setPayPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [payDatesLoading, setPayDatesLoading] = useState(true);
  const [payDateError, setPayDateError] = useState(null);
  const [selectedPayMonth, setSelectedPayMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [selectedPayWindowIndex, setSelectedPayWindowIndex] = useState(null);
  const [offsetWeeks, setOffsetWeeks] = useState(1);
  const [sspApproveBusy, setSspApproveBusy] = useState({});
  const [manualDateOverride, setManualDateOverride] = useState(false);

  const user = useContext(AuthContext);

  useEffect(() => {
    const fetchClockIns = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/allclockins', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        console.log("🔄 Fetched clockin data via API", data);
        setClockIns(data.data);
        setSick(data.sick || []);
        setHoliday(data.holiday || []);
      } catch (error) {
        console.error('Failed to fetch clock ins:', error);
      }
    };
    fetchClockIns();
  }, []);

  useEffect(() => {
    const fetchPayDates = async () => {
      setPayDatesLoading(true);
      setPayDateError(null);
      try {
        const response = await fetch('https://api.insightos.co.uk/api/paydates', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const payload = await response.json();
        const extracted = Array.isArray(payload.data)
          ? payload.data.filter((policy) => policy?.payDatePayload?.dates?.length)
          : [];
        setPayPolicies(extracted);
        setSelectedPolicyId((prev) => {
          if (prev) return prev;
          return extracted[0]?.id ?? null;
        });
      } catch (error) {
        console.error('Failed to fetch pay dates:', error);
        setPayDateError('Unable to load payroll schedule right now.');
      } finally {
        setPayDatesLoading(false);
      }
    };
    fetchPayDates();
  }, []);

  function parseTime(timeStr) {
    if (!timeStr) return null;
    const [h, m, s = '0'] = timeStr.split(':');
    return new Date(1970, 0, 1, Number(h), Number(m), Number(s));
  }

  function getHolidayDailyHours(person) {
    if (person.ContractedDays && person.ContractedHours) {
      let hours = Number(person.ContractedHours) / Number(person.ContractedDays);
      if (person.Holiday === 1 && person.HolidayHalfDay === 1) {
        hours = hours / 2;
      }
      return hours;
    }
    return 0;
  }

  function getHolidayDailyPay(person) {
    if (person.ContractedDays && person.ContractedHours && person.HourlyRate) {
      let pay = getHolidayDailyHours(person) * Number(person.HourlyRate);
      return pay;
    }
    return 0;
  }

  function isLongShift(person, endSource = null) {
    if (!person.StartTime || (!person.EndTime && !person.ClockOut)) return false;
    const start = parseTime(person.StartTime);
    const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
    let chosen = endSource || endSourceMap[key] || 'EndTime';
    let end = null;
    if (chosen === 'ClockOut' && person.ClockOut) end = parseTime(person.ClockOut);
    if (!end && person.EndTime) end = parseTime(person.EndTime);
    if (!end) return false;
    const hours = (end - start) / 36e5;
    return hours >= 6;
  }

  function getPayBeforeBreak(person, endSource = null) {
    if (!person.StartTime || (!person.EndTime && !person.ClockOut) || !person.HourlyRate) return 0;
    const start = parseTime(person.StartTime);
    let chosen = endSource;
    if (!chosen) {
      const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
      chosen = endSourceMap[key] || 'EndTime';
    }
    let end = null;
    if (chosen === 'ClockOut' && person.ClockOut) end = parseTime(person.ClockOut);
    if (!end && person.EndTime) end = parseTime(person.EndTime);
    if (!end) return 0;
    const hours = (end - start) / 36e5;
    return Math.max(0, hours) * person.HourlyRate;
  }

  function getTotalPay(person, endSource = null) {
    if (!person.StartTime || (!person.EndTime && !person.ClockOut) || !person.HourlyRate) return 0;
    const start = parseTime(person.StartTime);
    let chosen = endSource;
    if (!chosen) {
      const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
      chosen = endSourceMap[key] || 'EndTime';
    }
    let end = null;
    if (chosen === 'ClockOut' && person.ClockOut) end = parseTime(person.ClockOut);
    if (!end && person.EndTime) end = parseTime(person.EndTime);
    if (!end) return 0;
    let hours = (end - start) / 36e5;
    if (isLongShift(person, endSource)) {
      const unPaidBreakMinutes = person.UnpaidBreak || 0;
      hours = Math.max(0, hours - unPaidBreakMinutes / 60);
    }
    return hours * person.HourlyRate;
  }

  function getTotalHours(person, endSource = null) {
    if (!person.StartTime || (!person.EndTime && !person.ClockOut)) return 0;
    const start = parseTime(person.StartTime);
    const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
    let chosen = endSource || endSourceMap[key] || 'EndTime';
    let end = null;
    if (chosen === 'ClockOut' && person.ClockOut) end = parseTime(person.ClockOut);
    if (!end && person.EndTime) end = parseTime(person.EndTime);
    if (!end) return 0;
    let hours = (end - start) / 36e5;
    if (isLongShift(person, endSource)) {
      const unPaidBreakMinutes = person.UnpaidBreak || 0;
      hours = Math.max(0, hours - unPaidBreakMinutes / 60);
    }
    return hours;
  }

  const staffMap = {};
  clockIns.forEach(p => { staffMap[p.StaffId] = p.StaffName; });
  sick.forEach(p => { staffMap[p.StaffId] = p.Name || p.StaffName; });
  holiday.forEach(p => { staffMap[p.StaffId] = p.Name || p.StaffName; });
  const uniqueStaffNames = Object.values(staffMap);

  const filteredClockIns = clockIns.filter((person) => {
    const date = new Date(person.Date);
    const formattedDate = format(date, 'yyyy-MM-dd');
    const isWithinDateRange = (!startDate || formattedDate >= startDate) && (!endDate || formattedDate <= endDate);
    const isSelectedStaff = !selectedStaff || person.StaffName === selectedStaff;
    return isWithinDateRange && isSelectedStaff;
  });

  const filteredSick = sick.filter((person) => {
    const date = new Date(person.ShiftDate);
    const formattedDate = format(date, 'yyyy-MM-dd');
    const isWithinDateRange = (!startDate || formattedDate >= startDate) && (!endDate || formattedDate <= endDate);
    const isSelectedStaff = !selectedStaff || (person.Name || person.StaffName) === selectedStaff;
    return isWithinDateRange && isSelectedStaff;
  });

  const filteredHoliday = holiday.filter((person) => {
    const date = new Date(person.ShiftDate);
    const formattedDate = format(date, 'yyyy-MM-dd');
    const isWithinDateRange = (!startDate || formattedDate >= startDate) && (!endDate || formattedDate <= endDate);
    const isSelectedStaff = !selectedStaff || (person.Name || person.StaffName) === selectedStaff;
    return isWithinDateRange && isSelectedStaff;
  });

  const clockInMap = {};
  clockIns.forEach(person => {
    clockInMap[`${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`] = person;
  });

  const selectedPolicy = useMemo(
    () => payPolicies.find((policy) => policy.id === selectedPolicyId) || null,
    [payPolicies, selectedPolicyId]
  );

  const policyDates = selectedPolicy?.payDatePayload?.dates ?? [];

  const payWindows = useMemo(() => {
    if (!Array.isArray(policyDates) || policyDates.length < 2) return [];
    const windows = [];
    for (let i = 0; i < policyDates.length - 1; i += 1) {
      const start = policyDates[i];
      const end = policyDates[i + 1];
      windows.push({ key: `${start}_${end}`, start, end });
    }
    return windows;
  }, [policyDates]);

  useEffect(() => {
    if (!payWindows.length) {
      setSelectedPayWindowIndex(null);
      return;
    }
    setSelectedPayWindowIndex((prev) => {
      if (prev !== null && payWindows[prev]) return prev;
      return 0;
    });
  }, [payWindows]);

  useEffect(() => {
    if (!payWindows.length || !selectedPayMonth) return;
    const [year, month] = selectedPayMonth.split('-').map((value) => Number(value));
    if (!year || !month) return;
    const monthStart = startOfMonth(new Date(year, month - 1, 1));
    const monthEnd = endOfMonth(monthStart);
    const idx = payWindows.findIndex((window) => {
      const start = parseISO(window.start);
      const end = parseISO(window.end);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      return (
        isWithinInterval(start, { start: monthStart, end: monthEnd }) ||
        isWithinInterval(end, { start: monthStart, end: monthEnd })
      );
    });
    if (idx !== -1 && idx !== selectedPayWindowIndex) {
      setSelectedPayWindowIndex(idx);
    }
  }, [selectedPayMonth, payWindows, selectedPayWindowIndex]);

  const payDatesForMonth = useMemo(() => {
    if (!policyDates.length || !selectedPayMonth) return [];
    return policyDates.filter((isoDate) => isoDate.startsWith(selectedPayMonth));
  }, [policyDates, selectedPayMonth]);

  const selectedMonthLabel = useMemo(() => {
    if (!selectedPayMonth) return '';
    const [year, month] = selectedPayMonth.split('-').map((value) => Number(value));
    if (!year || !month) return '';
    return format(new Date(year, month - 1, 1), 'MMMM yyyy');
  }, [selectedPayMonth]);

  const selectedPayWindow = selectedPayWindowIndex !== null ? payWindows[selectedPayWindowIndex] : null;

  const payWindowRange = useMemo(() => {
    if (!selectedPayWindow) return null;
    const lastPayDate = parseISO(selectedPayWindow.start);
    const nextPayDate = parseISO(selectedPayWindow.end);
    if (Number.isNaN(lastPayDate.getTime()) || Number.isNaN(nextPayDate.getTime())) return null;
    const offsetValue = Number(offsetWeeks);
    const offsetDays = Number.isFinite(offsetValue) ? Math.max(0, offsetValue) * 7 : 0;
    const effectiveStart = subDays(lastPayDate, offsetDays);
    const effectiveEnd = subDays(nextPayDate, offsetDays);
    return { lastPayDate, nextPayDate, effectiveStart, effectiveEnd, offsetDays };
  }, [selectedPayWindow, offsetWeeks]);

  // Auto-sync dates when pay window changes, unless manually overridden
  useEffect(() => {
    if (manualDateOverride) return;
    if (!payWindowRange) return;
    setStartDate(format(payWindowRange.effectiveStart, 'yyyy-MM-dd'));
    setEndDate(format(payWindowRange.effectiveEnd, 'yyyy-MM-dd'));
  }, [payWindowRange, manualDateOverride]);

  const formatIsoDate = (isoString, fallback = '—') => {
    if (!isoString) return fallback;
    const parsed = parseISO(isoString);
    if (Number.isNaN(parsed.getTime())) return fallback;
    return format(parsed, 'dd MMM yyyy');
  };

  const handlePolicyChange = (event) => {
    const nextValue = Number(event.target.value);
    setSelectedPolicyId(Number.isNaN(nextValue) ? null : nextValue);
    setManualDateOverride(false);
  };

  const handlePayWindowChange = (event) => {
    const key = event.target.value;
    const nextIndex = payWindows.findIndex((window) => window.key === key);
    setSelectedPayWindowIndex(nextIndex === -1 ? null : nextIndex);
    setManualDateOverride(false);
  };

  const handleOffsetChange = (event) => {
    const next = Number(event.target.value);
    if (Number.isNaN(next) || next < 0) {
      setOffsetWeeks(0);
    } else {
      setOffsetWeeks(next);
    }
    setManualDateOverride(false);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setManualDateOverride(true);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setManualDateOverride(true);
  };

  function toggleEndSource(key, source) {
    setEndSourceMap(prev => ({ ...prev, [key]: source }));
  }

  function getStaffSummary() {
    const summary = {};
    filteredClockIns.forEach(person => {
      if (!summary[person.StaffId]) {
        summary[person.StaffId] = {
          Name: person.StaffName,
          StaffId: person.StaffId,
          TotalHours: 0,
          TotalPay: 0,
        };
      }
      const hoursWorked = getTotalHours(person);
      summary[person.StaffId].TotalHours += hoursWorked;
      summary[person.StaffId].TotalPay += getTotalPay(person);
    });
    return Object.values(summary);
  }

  const getAverageWeeklyEarnings = (staffId, sicknessDate) => {
    const sickness = new Date(sicknessDate);
    if (Number.isNaN(sickness.getTime())) return 0;
    const periodStart = subDays(sickness, 56);
    const priorRows = clockIns.filter((entry) => {
      if (String(entry.StaffId) !== String(staffId)) return false;
      const workedDate = new Date(entry.Date);
      if (Number.isNaN(workedDate.getTime())) return false;
      return workedDate < sickness && workedDate >= periodStart;
    });
    const totalEarnings = priorRows.reduce((sum, entry) => sum + getTotalPay(entry), 0);
    return totalEarnings / 8;
  };

  const getSspDailyRate = (averageWeeklyEarnings, qualifyingDays) => {
    const cappedWeekly = Math.min(UK_SSP_WEEKLY_CAP, averageWeeklyEarnings * 0.8);
    const resolvedQualifyingDays = Math.max(1, toNumber(qualifyingDays, DEFAULT_QUALIFYING_DAYS));
    return Math.max(0, cappedWeekly / resolvedQualifyingDays);
  };

  const getContractedWeeklyEarnings = (row) => {
    const contractedHours = toNumber(row?.ContractedHours, 0);
    const hourlyRate = toNumber(row?.StaffHourlyRate ?? row?.HourlyRate, 0);
    if (contractedHours <= 0 || hourlyRate <= 0) return 0;
    return contractedHours * hourlyRate;
  };

  const toggleSspApproval = async (row) => {
    if (!row?.sickleave_id) return;
    const nextApproved = Number(row.SSPApproved) === 1 ? 0 : 1;
    setSspApproveBusy((prev) => ({ ...prev, [row.sickleave_id]: true }));
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/sickleave/ssp-approve/${row.sickleave_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ approved: nextApproved }),
      });
      if (!response.ok) throw new Error(`Failed with status ${response.status}`);
      setSick((prev) =>
        prev.map((entry) =>
          Number(entry.sickleave_id) === Number(row.sickleave_id)
            ? { ...entry, SSPApproved: nextApproved }
            : entry
        )
      );
    } catch (error) {
      console.error('Failed to update SSP approval:', error);
    } finally {
      setSspApproveBusy((prev) => ({ ...prev, [row.sickleave_id]: false }));
    }
  };

  function getSickTable() {
    const sickByStaff = {};
    filteredSick.forEach(person => {
      const key = `${person.StaffId}_${format(new Date(person.ShiftDate), 'yyyy-MM-dd')}`;
      if (!clockInMap[key]) {
        if (!sickByStaff[person.StaffId]) sickByStaff[person.StaffId] = [];
        sickByStaff[person.StaffId].push(person);
      }
    });
    const rows = [];
    Object.entries(sickByStaff).forEach(([staffId, sickArr]) => {
      sickArr.sort((a, b) => new Date(a.ShiftDate) - new Date(b.ShiftDate));
      for (let i = 0; i < sickArr.length; i += 1) {
        const day = sickArr[i];
        const averageWeeklyEarnings = getAverageWeeklyEarnings(staffId, day.ShiftDate);
        const zeroHours = isZeroHoursContract(day.ContractType);
        const qualifyingDays = toNumber(day.ContractedDays, DEFAULT_QUALIFYING_DAYS);
        const contractedWeeklyEarnings = getContractedWeeklyEarnings(day);
        const weeklyEarningsForSsp = zeroHours
          ? averageWeeklyEarnings
          : (contractedWeeklyEarnings > 0 ? contractedWeeklyEarnings : averageWeeklyEarnings);
        const reachesThreshold = zeroHours ? averageWeeklyEarnings >= ZERO_HOURS_WEEKLY_THRESHOLD : true;
        const sspDailyRate = getSspDailyRate(weeklyEarningsForSsp, qualifyingDays);
        const paid = reachesThreshold;

        rows.push({
          Name: day.Name || day.StaffName,
          StaffId: staffId,
          NationalInsuranceNumber: day.NationalInsuranceNumber || '',
          Date: day.ShiftDate,
          SSP: paid ? 'Yes' : 'No',
          SspDailyAmount: paid ? sspDailyRate : 0,
          AvgWeeklyEarnings: averageWeeklyEarnings,
          ZeroHours: zeroHours ? 1 : 0,
          ThresholdMet: reachesThreshold ? 1 : 0,
          sickleave_id: day.sickleave_id || null,
          StartDate: day.StartDate || day.sickleave_StartDate || null,
          ReturnDate: day.ReturnDate || day.sickleave_ReturnDate || null,
          SelfCert: day.SelfCert ?? day.sickleave_SelfCert ?? 0,
          DRLetter: day.DRLetter ?? day.sickleave_DRLetter ?? 0,
          MeetingHeld: day.MeetingHeld ?? day.sickleave_MeetingHeld ?? 0,
          SSPApproved: day.SSPApproved ?? day.sickleave_SSPApproved ?? 0,
          HasSickLeaveForm: Boolean(day.sickleave_id),
        });
      }
    });
    return rows;
  }

  function getHolidayTable() {
    const rows = [];
    filteredHoliday.forEach(person => {
      const key = `${person.StaffId}_${format(new Date(person.ShiftDate), 'yyyy-MM-dd')}`;
      if (!clockInMap[key]) {
        let hours = getHolidayDailyHours(person);
        let pay = getHolidayDailyPay(person);
        rows.push({
          Name: person.Name || person.StaffName,
          StaffId: person.StaffId,
          NationalInsuranceNumber: person.NationalInsuranceNumber || '',
          Date: person.ShiftDate,
          Hours: hours,
          Pay: pay,
        });
      }
    });
    return rows;
  }

  const summaryArray = getStaffSummary();
  const sickTable = getSickTable();
  const holidayTable = getHolidayTable();

  const payWindowTotals = useMemo(() => {
    const totalPay = filteredClockIns.reduce((sum, person) => sum + getTotalPay(person), 0);
    const totalHours = filteredClockIns.reduce((sum, person) => sum + getTotalHours(person), 0);
    return {
      totalPay,
      totalHours,
      entries: filteredClockIns.length
    };
  }, [filteredClockIns, endSourceMap]);

  const sageCsvData = [];
  const xeroCsvData = [];

  filteredClockIns.forEach(person => {
    const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
    const chosen = endSourceMap[key] || 'EndTime';
    const units = getTotalHours(person, chosen);
    const amount = getTotalPay(person, chosen);
    sageCsvData.push({
      'Employee Reference': person.StaffId,
      'Date': format(new Date(person.Date), 'dd/MM/yyyy'),
      'Pay Code': 'NORMAL',
      'Units': units.toFixed(2),
      'Rate': Number(person.HourlyRate).toFixed(2),
      'Amount': amount.toFixed(2),
      'Narrative': `${person.StaffName || ''} ${person.NationalInsuranceNumber ? `(${person.NationalInsuranceNumber})` : ''} - Worked shift (end used: ${chosen})`.trim(),
    });
    xeroCsvData.push({
      'Date': format(new Date(person.Date), 'dd/MM/yyyy'),
      'Employee Name': person.StaffName || '',
      'Employee Reference': person.StaffId,
      'National Insurance Number': person.NationalInsuranceNumber || '',
      'Pay Item': 'NORMAL',
      'Units': units.toFixed(2),
      'Rate': Number(person.HourlyRate || 0).toFixed(2),
      'Amount': amount.toFixed(2),
      'Status': 'Worked',
      'Description': `Shift worked (end used: ${chosen})`,
    });
  });

  sickTable.forEach(row => {
    const amount = row.SSP === 'Yes' ? row.SspDailyAmount : 0;
    sageCsvData.push({
      'Employee Reference': row.StaffId,
      'Date': format(new Date(row.Date), 'dd/MM/yyyy'),
      'Pay Code': 'SSP',
      'Units': '1.00',
      'Rate': amount.toFixed(2),
      'Amount': amount.toFixed(2),
      'Narrative': `${row.Name || ''} ${row.NationalInsuranceNumber ? `(${row.NationalInsuranceNumber})` : ''} - Statutory Sick Pay (${row.SSP === 'Yes' ? 'Eligible' : 'Not eligible'})`.trim(),
    });
    xeroCsvData.push({
      'Date': format(new Date(row.Date), 'dd/MM/yyyy'),
      'Employee Name': row.Name || '',
      'Employee Reference': row.StaffId,
      'National Insurance Number': row.NationalInsuranceNumber || '',
      'Pay Item': 'SSP',
      'Units': '1.00',
      'Rate': amount.toFixed(2),
      'Amount': amount.toFixed(2),
      'Status': row.SSP,
      'Description': `SSP ${row.SSP === 'Yes' ? 'eligible' : 'not eligible'}`,
    });
  });

  holidayTable.forEach(row => {
    sageCsvData.push({
      'Employee Reference': row.StaffId,
      'Date': format(new Date(row.Date), 'dd/MM/yyyy'),
      'Pay Code': 'HOLIDAY',
      'Units': row.Hours.toFixed(2),
      'Rate': row.Hours === 0 ? '0.00' : (row.Pay / row.Hours).toFixed(2),
      'Amount': row.Pay.toFixed(2),
      'Narrative': `${row.Name || ''} ${row.NationalInsuranceNumber ? `(${row.NationalInsuranceNumber})` : ''} - Holiday Pay`.trim(),
    });
    xeroCsvData.push({
      'Date': format(new Date(row.Date), 'dd/MM/yyyy'),
      'Employee Name': row.Name || '',
      'Employee Reference': row.StaffId,
      'National Insurance Number': row.NationalInsuranceNumber || '',
      'Pay Item': 'HOLIDAY',
      'Units': row.Hours.toFixed(2),
      'Rate': row.Hours === 0 ? '0.00' : (row.Pay / row.Hours).toFixed(2),
      'Amount': row.Pay.toFixed(2),
      'Status': 'Approved',
      'Description': 'Holiday pay',
    });
  });

  return (
    <>
      {showNav && <StaffNav />}
      <div className="px-4 sm:px-6 lg:px-8">
        <section className="mt-6 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-emerald-100/60 p-4 sm:p-6 shadow">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[220px] flex-1">
              <label className="block text-sm font-medium text-emerald-900">Policy</label>
              <select
                value={selectedPolicyId ?? ''}
                onChange={handlePolicyChange}
                disabled={!payPolicies.length}
                className="mt-1 w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-400 focus:outline-none"
              >
                {!payPolicies.length && <option value="">No payroll schedules saved yet</option>}
                {payPolicies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.payDatePayload?.config?.label || `Policy #${policy.id} — ${policy.paySchedule}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-emerald-900">Month</label>
              <input
                type="month"
                value={selectedPayMonth}
                onChange={(event) => setSelectedPayMonth(event.target.value)}
                className="mt-1 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-400 focus:outline-none"
                min="2020-01"
              />
            </div>
            <div className="min-w-[220px] flex-1">
              <label className="block text-sm font-medium text-emerald-900">Pay window</label>
              <select
                value={selectedPayWindow?.key ?? ''}
                onChange={handlePayWindowChange}
                disabled={!payWindows.length}
                className="mt-1 w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-400 focus:outline-none"
              >
                {!payWindows.length && <option value="">Add at least two pay dates</option>}
                {payWindows.map((window) => (
                  <option key={window.key} value={window.key}>
                    {`${formatIsoDate(window.start)} → ${formatIsoDate(window.end)}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-emerald-900">Offset (weeks)</label>
              <input
                type="number"
                min="0"
                max="6"
                step="1"
                value={offsetWeeks}
                onChange={handleOffsetChange}
                className="mt-1 w-28 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-emerald-400 focus:outline-none"
              />
            </div>
          </div>
          {payDateError && <p className="mt-4 text-sm text-red-600">{payDateError}</p>}
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl bg-white/80 p-4 shadow-inner">
              <h3 className="text-sm font-semibold text-emerald-900">
                Pay dates in {selectedMonthLabel || 'selected month'}
              </h3>
              {payDatesLoading ? (
                <p className="mt-3 text-sm text-gray-600">Fetching schedule…</p>
              ) : payDatesForMonth.length ? (
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  {payDatesForMonth.map((date) => (
                    <li key={date} className="flex items-center justify-between rounded-xl border border-emerald-100 px-3 py-2">
                      <span>{formatIsoDate(date)}</span>
                      <span className="text-xs uppercase tracking-wide text-emerald-500">Due</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-gray-600">No pay dates fall in this month.</p>
              )}
            </div>
            <div className="rounded-2xl bg-slate-900 p-4 text-white shadow-inner">
              <h3 className="text-sm font-semibold text-emerald-200">Selected pay period</h3>
              {payWindowRange ? (
                <div className="mt-3 space-y-3 text-sm text-slate-100">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-300">Last pay date</p>
                    <p className="text-base font-semibold">{formatIsoDate(selectedPayWindow.start)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-300">Next pay date</p>
                    <p className="text-base font-semibold">{formatIsoDate(selectedPayWindow.end)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-300">Data window (offset {offsetWeeks}w)</p>
                    <p className="text-base font-semibold">
                      {format(payWindowRange.effectiveStart, 'dd MMM yyyy')} → {format(payWindowRange.effectiveEnd, 'dd MMM yyyy')}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-3">
                    <p className="text-xs uppercase tracking-wide text-emerald-200">Current filtered cost</p>
                    <p className="text-2xl font-bold">£{payWindowTotals.totalPay.toFixed(2)}</p>
                    <p className="text-xs text-emerald-100">
                      {payWindowTotals.totalHours.toFixed(2)} hrs · {payWindowTotals.entries} records
                    </p>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-200">Add at least two pay dates to view payroll windows.</p>
              )}
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 my-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <select
              value={selectedStaff}
              onChange={e => setSelectedStaff(e.target.value)}
              className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            >
              <option value="">All</option>
              {uniqueStaffNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
              <ChevronDownIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
              />
            </select>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <CSVLink
              data={sageCsvData}
              filename="sage_upload_full.csv"
              headers={[
                { label: 'Employee Reference', key: 'Employee Reference' },
                { label: 'Date', key: 'Date' },
                { label: 'Pay Code', key: 'Pay Code' },
                { label: 'Units', key: 'Units' },
                { label: 'Rate', key: 'Rate' },
                { label: 'Amount', key: 'Amount' },
                { label: 'Narrative', key: 'Narrative' },
              ]}
              className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <DocumentArrowDownIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Download Sage CSV
            </CSVLink>
            <CSVLink
              data={xeroCsvData}
              filename="xero_upload_full.csv"
              headers={[
                { label: 'Date', key: 'Date' },
                { label: 'Employee Name', key: 'Employee Name' },
                { label: 'Employee Reference', key: 'Employee Reference' },
                { label: 'National Insurance Number', key: 'National Insurance Number' },
                { label: 'Pay Item', key: 'Pay Item' },
                { label: 'Units', key: 'Units' },
                { label: 'Rate', key: 'Rate' },
                { label: 'Amount', key: 'Amount' },
                { label: 'Status', key: 'Status' },
                { label: 'Description', key: 'Description' },
              ]}
              className="inline-flex items-center gap-x-2 rounded-md bg-emerald-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
            >
              <DocumentArrowDownIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Download Xero CSV
            </CSVLink>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-violet-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock In</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock Out</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break Start</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break End</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break Applied</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Before Break</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Pay</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Late</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClockIns.map((person) => {
                      const key = `${person.StaffId}_${format(new Date(person.Date), 'yyyy-MM-dd')}`;
                      const chosen = endSourceMap[key] || 'EndTime';
                      const units = getTotalHours(person, chosen);
                      const amount = getTotalPay(person, chosen);
                      const payBeforeBreak = getPayBeforeBreak(person, chosen);
                      const breakApplied = isLongShift(person, chosen);
                      return (
                        <tr key={person.shift_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{person.StaffName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(person.Date), 'do MMMM yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.StartTime || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.EndTime || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.ClockIn || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.ClockOut || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.BreakStart || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.BreakEnd || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              breakApplied ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {breakApplied ? 'Yes' : 'No'}
                            </span>
                            {breakApplied && (
                              <div className="text-xs text-gray-600 mt-1">{person.UnpaidBreak || 0} mins</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            £{payBeforeBreak.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div className="text-sm font-medium">£{amount.toFixed(2)}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <select
                                  value={chosen}
                                  onChange={e => toggleEndSource(key, e.target.value)}
                                  className="rounded-md border px-2 py-1 text-sm"
                                >
                                  <option value="EndTime">Shift End</option>
                                  <option value="ClockOut">ClockOut</option>
                                </select>
                                <div className="text-sm text-gray-600">Used: {chosen === 'ClockOut' ? (person.ClockOut || person.EndTime || 'N/A') : (person.EndTime || person.ClockOut || 'N/A')}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {units.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {person.Late === 1 ? 'Yes' : person.Late === 0 ? 'No' : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-2">Summary</h2>
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Pay</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summaryArray.map((row) => (
                  <tr key={row.StaffId}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.Name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.TotalHours.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{row.TotalPay.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-2">Sick Days</h2>
          <details className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-amber-900">New SSP changes (from 6 April 2026)</summary>
            <div className="mt-2 space-y-1 text-sm text-amber-900">
              <p>SSP is paid from day one of sickness on qualifying days.</p>
              <p>Weekly SSP is the lower of £123.25 or 80% average weekly earnings from the prior 8 weeks.</p>
              <p>In this screen, zero-hours contracts are checked against an 8-week threshold of £125 average weekly earnings.</p>
              <p>SSP can still be paid when a SickLeave form is missing, provided eligibility checks pass.</p>
              <p>Approve SSP for matched SickLeave records using the button in the table below.</p>
            </div>
          </details>
          <div className="shadow border-b border-gray-200 sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-[1400px] divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SSP Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily SSP</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AWE (8w)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Form</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Return Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Cert</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dr Letter</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meeting Held</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approve</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sickTable.map((row, idx) => (
                    <tr key={row.StaffId + row.Date + idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.Name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{format(new Date(row.Date), 'do MMMM yyyy')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.SSP}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{row.SspDailyAmount.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{row.AvgWeeklyEarnings.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.HasSickLeaveForm ? 'Submitted' : 'Missing'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.StartDate ? format(new Date(row.StartDate), 'do MMM yyyy') : 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.ReturnDate ? format(new Date(row.ReturnDate), 'do MMM yyyy') : 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(row.SelfCert) === 1 ? 'Yes' : 'No'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(row.DRLetter) === 1 ? 'Yes' : 'No'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(row.MeetingHeld) === 1 ? 'Yes' : 'No'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.sickleave_id ? (
                          <button
                            type="button"
                            onClick={() => toggleSspApproval(row)}
                            disabled={sspApproveBusy[row.sickleave_id]}
                            className={`rounded-md px-3 py-1.5 text-xs font-semibold text-white ${
                              Number(row.SSPApproved) === 1 ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-indigo-600 hover:bg-indigo-500'
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                          >
                            {sspApproveBusy[row.sickleave_id]
                              ? 'Saving...'
                              : Number(row.SSPApproved) === 1
                                ? 'Approved'
                                : 'Approve'}
                          </button>
                        ) : (
                          row.SSP === 'Yes' ? 'Paid (no form)' : 'No SSP'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-2">Holiday Days</h2>
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {holidayTable.map((row, idx) => (
                  <tr key={row.StaffId + row.Date + idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.Name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{format(new Date(row.Date), 'do MMMM yyyy')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.Hours.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{row.Pay.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default ClockInTable;